import { useCallback, useMemo } from 'react';

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Popover } from 'antd';

import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon/Icon';

import styles from './ColumnVisibilityButton.module.css';
import { ColumnVisibilityItem } from './ColumnVisibilityItem';
import { useColumnVisibility } from './useColumnVisibility';

export type ColumnGroup<T extends string = string> = {
  title: string;
  columns: T[];
};

type ColumnVisibilityButtonProps<T extends string = string> = {
  defaultColumnOrder: T[];
  defaultVisibleColumns: T[];
  allColumnsMap: Record<T, { title: string; group?: string }>;
  columnGroups?: ColumnGroup<T>[];
  showGrouped?: boolean;
  buttonText?: string;
  columnOrderParamKey?: string;
  visibleColumnsParamKey?: string;
};

export const ColumnVisibilityButton = <T extends string = string>({
  defaultColumnOrder,
  defaultVisibleColumns,
  allColumnsMap,
  columnGroups,
  showGrouped = false,
  buttonText = '테이블 설정',
  columnOrderParamKey = 'columnOrder',
  visibleColumnsParamKey = 'columns',
}: ColumnVisibilityButtonProps<T>) => {
  const { state, columnConfigs, allColumnsVisible, handleOrderChange, handleToggleVisibility, handleShowAll } =
    useColumnVisibility({
      defaultColumnOrder,
      defaultVisibleColumns,
      allColumnsMap,
      columnOrderParamKey,
      visibleColumnsParamKey,
    });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = state.columnOrder.indexOf(active.id as T);
        const newIndex = state.columnOrder.indexOf(over.id as T);
        const newOrder = arrayMove(state.columnOrder, oldIndex, newIndex);
        handleOrderChange(newOrder);
      }
    },
    [state.columnOrder, handleOrderChange]
  );

  const groupedColumnConfigs = useMemo(() => {
    if (!showGrouped || !columnGroups || columnGroups.length === 0) {
      return [{ title: '', columns: columnConfigs }];
    }

    return columnGroups.map((group) => ({
      title: group.title,
      columns: columnConfigs.filter((config) => group.columns.includes(config.key as T)),
    }));
  }, [showGrouped, columnGroups, columnConfigs]);

  const columnVisibilityContent = useMemo(
    () => (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className={styles.columnVisibilityContent}>
          <SortableContext items={state.columnOrder} strategy={verticalListSortingStrategy}>
            <div className={styles.columnList}>
              {groupedColumnConfigs.map((group, groupIndex) => (
                <div key={groupIndex}>
                  {group.title && (
                    <div className={styles.groupTitle}>
                      {group.title} ({group.columns.length})
                    </div>
                  )}
                  {group.columns.map((config) => (
                    <ColumnVisibilityItem
                      key={config.key}
                      id={config.key}
                      columnConfig={config}
                      onToggleVisibility={handleToggleVisibility}
                    />
                  ))}
                </div>
              ))}
            </div>
          </SortableContext>
        </div>
        <div className={styles.showAllButtonContainer}>
          <Button
            variant="outlined"
            color="standard"
            onClick={handleShowAll}
            disabled={allColumnsVisible}
            className={styles.showAllButton}>
            모두 보기
          </Button>
        </div>
      </DndContext>
    ),
    [
      sensors,
      handleDragEnd,
      handleShowAll,
      allColumnsVisible,
      state.columnOrder,
      groupedColumnConfigs,
      handleToggleVisibility,
    ]
  );

  return (
    <Popover content={columnVisibilityContent} trigger="click" placement="bottomLeft" arrow={false}>
      <Button
        variant="outlined"
        color="standard"
        prefix={<Icon name="setting" size={16} />}
        className={styles.columnVisibilityButton}>
        {buttonText}
      </Button>
    </Popover>
  );
};
